import { SearchState } from "src/search/types";

export type QuestionInputOptions = Partial<{
  disableSelect: boolean;
  disableGroupBys: boolean;
  disableMeasures: boolean;
  disableSearchTerm: boolean;
  disableDateRange: boolean;
  disableFilters: boolean;
  disableCompareWith: boolean;
  disableFormula: boolean;
  disableHaving: boolean;
  disableSnippets: boolean;
  disableSuggestions: boolean;
  disableSuggestedQuestions: boolean;
  addFromTeamSnippet: boolean;
}>;

export enum Clauses {
  "COMPARE_WITH" = "compare-with",
  "DATE_RANGE" = "date-range",
  "FILTER_CONDITION" = "filter-condition",
  "GROUP_BY" = "group-by",
  "MEASURE_BY" = "measure-by",
  "FORMULA" = "column-formula",
  "HAVING" = "column-having",
  "SEARCH_TERM" = "search-term",
  "SELECT" = "select",
}

export const OrderedPrimaryClauseTypes = [
  Clauses.SELECT,
  Clauses.DATE_RANGE,
  Clauses.FILTER_CONDITION,
  Clauses.GROUP_BY,
  Clauses.SEARCH_TERM,
];

export const OrderedSecondaryClauseTypes = [
  Clauses.MEASURE_BY,
  Clauses.FORMULA,
  Clauses.HAVING,
  Clauses.COMPARE_WITH,
];

export const OrderedClauseTypes = [
  ...OrderedPrimaryClauseTypes,
  ...OrderedSecondaryClauseTypes,
];

export type InputClause =
  | CompareWithInputClause
  | DateRangeInputClause
  | FilterConditionInputClause
  | GroupByInputClause
  | MeasureByInputClause
  | SearchInputClause
  | SelectInputClause
  | FormulaInputClause
  | HavingInputClause;

export type CompareWithInputClause = TypedInputClause<
  Clauses.COMPARE_WITH,
  SearchState["compareWith"] | undefined
>;
export type DateRangeInputClause = TypedInputClause<
  Clauses.DATE_RANGE,
  Pick<SearchState, "duration" | "startDate" | "endDate"> | undefined
>;
export type FilterConditionInputClause = TypedInputClause<
  Clauses.FILTER_CONDITION,
  Exclude<SearchState["filters"], undefined>[number] | undefined
>;
export type GroupByInputClause = TypedInputClause<
  Clauses.GROUP_BY,
  Exclude<SearchState["groupBys"], undefined>[number] | undefined
>;
export type MeasureByInputClause = TypedInputClause<
  Clauses.MEASURE_BY,
  Exclude<SearchState["aggregations"], undefined>[number] | undefined
>;
export type FormulaInputClause = TypedInputClause<
  Clauses.FORMULA,
  Exclude<SearchState["formulas"], undefined>[number] | undefined
>;

export type HavingInputClause = TypedInputClause<
  Clauses.HAVING,
  Exclude<SearchState["having"], undefined>[number] | undefined
>;

export type SearchInputClause = TypedInputClause<
  Clauses.SEARCH_TERM,
  SearchState["search"]
>;
export type SelectInputClause = TypedInputClause<
  Clauses.SELECT,
  Exclude<SearchState["select"], undefined>[number] | undefined
>;

export function isCompareWithInputClause(
  clause: InputClause
): clause is CompareWithInputClause {
  return clause.type === Clauses.COMPARE_WITH;
}
export function isColumnFormulaClause(
  clause: InputClause
): clause is FormulaInputClause {
  return clause.type === Clauses.FORMULA;
}
export function isDateRangeInputClause(
  clause: InputClause
): clause is DateRangeInputClause {
  return clause.type === Clauses.DATE_RANGE;
}
export function isFilterConditionInputClause(
  clause: InputClause
): clause is FilterConditionInputClause {
  return clause.type === Clauses.FILTER_CONDITION;
}
export function isFormulaInputClause(
  clause: InputClause
): clause is FormulaInputClause {
  return clause.type === Clauses.FORMULA;
}
export function isHavingInputClause(
  clause: InputClause
): clause is HavingInputClause {
  return clause.type === Clauses.HAVING;
}
export function isGroupByClause(
  clause: InputClause
): clause is GroupByInputClause {
  return clause.type === Clauses.GROUP_BY;
}
export function isMeasureByClause(
  clause: InputClause
): clause is MeasureByInputClause {
  return clause.type === Clauses.MEASURE_BY;
}
export function isSearchInputClause(
  clause: InputClause
): clause is SearchInputClause {
  return clause.type === Clauses.SEARCH_TERM;
}
export function isSelectInputClause(
  clause: InputClause
): clause is SelectInputClause {
  return clause.type === Clauses.SELECT;
}

type TypedInputClause<Type extends Clauses, Value> = {
  type: Type;
  key: string;
  status: "init" | "ready";
  value: Value;
};

export const DATA_FOCUS_ADD = "add";

export function data_focus(id?: string) {
  return id ? `[data-focus='${id}']` : `[data-focus]`;
}

export type UseQuestionInputClauses = {
  list: InputClause[];
  reset(): void;
  addClause(
    partial: Pick<InputClause, "key" | "type" | "value">,
    status: "init" | "ready"
  ): void;
  clearClauses(): void;
  removeClause(clause: InputClause): void;
  updateClause(clause: InputClause): void;
  mergeStateIntoClauses(state: SearchState): void;
  value: SearchState;
  isSeeding: boolean;
  setValue(state: SearchState): void;
};

export type ClauseMenuItem = {
  key: string;
  label: React.ReactNode;
  keywords?: string[];
  value?: string;
  labelKey?: string;
  disabled?: boolean;
  aesthetic?: boolean;
  metaColor?: string;
  isOption?: boolean;
  isPartial?: boolean;
  selectedOption?: boolean;
  kind?: string;
  onSelect(): void;
};
